import type { RegionHazard } from '@/db/types';

/**
 * v1 hazard defaults: rough first-digit ZIP → likely-relevant hazards.
 *
 * Real FEMA + NOAA ZIP→hazard tables would land here later. For onboarding
 * we just need a "did we pre-check the obvious ones?" answer that the user
 * can refine via chips. Better to over-include (user unchecks what doesn't
 * apply) than to leave a Floridian without hurricane prep checked.
 *
 * Two-digit refinements override the one-digit defaults for cases where
 * the leading digit straddles very different hazard regions (e.g. Mountain
 * West 8xx vs Pacific Coast 9xx).
 */
const HAZARDS_BY_LEADING_DIGIT: Record<string, RegionHazard[]> = {
  '0': ['hurricane', 'ice-storm', 'flood'],      // New England + PR/VI
  '1': ['ice-storm', 'flood'],                   // NY, PA, DE
  '2': ['hurricane', 'tornado', 'flood'],        // Mid-Atlantic + Carolinas
  '3': ['hurricane', 'tornado', 'flood'],        // Southeast (FL/GA/AL/MS/TN)
  '4': ['tornado', 'ice-storm', 'flood'],        // KY/IN/MI/OH
  '5': ['tornado', 'ice-storm'],                 // Upper Midwest
  '6': ['tornado', 'ice-storm', 'flood'],        // Central Plains
  '7': ['hurricane', 'tornado', 'flood'],        // South Central (TX/LA/OK/AR)
  '8': ['wildfire', 'earthquake'],               // Mountain West
  '9': ['wildfire', 'earthquake', 'flood'],      // West Coast (CA/OR/WA/NV/AK/HI)
};

/**
 * Two-digit prefix overrides. Sparse on purpose — only fill in cases
 * where the one-digit guess is meaningfully wrong for that bucket.
 */
const HAZARDS_BY_TWO_DIGIT: Record<string, RegionHazard[]> = {
  // Florida — every ZIP in 32-34 gets hurricane + flood (peninsula).
  '32': ['hurricane', 'tornado', 'flood'],
  '33': ['hurricane', 'flood'],
  '34': ['hurricane', 'flood'],
  // Tornado Alley core — central Plains.
  '66': ['tornado', 'ice-storm'],
  '67': ['tornado', 'ice-storm'],
  '73': ['tornado', 'ice-storm'],
  '74': ['tornado', 'ice-storm'],
  // Coastal Texas vs interior — coastal hits hurricane harder.
  '77': ['hurricane', 'tornado', 'flood'],
  '78': ['hurricane', 'tornado', 'flood'],
  // California — wildfire + earthquake; interior valleys add flood risk.
  '90': ['wildfire', 'earthquake'],
  '91': ['wildfire', 'earthquake'],
  '92': ['wildfire', 'earthquake'],
  '93': ['wildfire', 'earthquake', 'flood'],
  '94': ['earthquake', 'wildfire', 'flood'],
  '95': ['wildfire', 'earthquake', 'flood'],
  // Pacific Northwest — earthquake + wildfire dominant.
  '97': ['earthquake', 'wildfire'],
  '98': ['earthquake', 'wildfire'],
  // Hawaii — hurricane + flood + earthquake (volcanic).
  '96': ['hurricane', 'earthquake', 'flood'],
};

export function lookupRegionHazards(zip: string): RegionHazard[] {
  const clean = zip.trim();
  if (!/^\d{5}$/.test(clean)) return [];
  const two = HAZARDS_BY_TWO_DIGIT[clean.slice(0, 2)];
  if (two) return two;
  return HAZARDS_BY_LEADING_DIGIT[clean[0]] ?? [];
}

export const REGION_HAZARD_LABEL: Record<RegionHazard, string> = {
  hurricane: 'Hurricane',
  tornado: 'Tornado',
  wildfire: 'Wildfire',
  earthquake: 'Earthquake',
  flood: 'Flood',
  'ice-storm': 'Ice storm',
};

export const REGION_HAZARD_ORDER: RegionHazard[] = [
  'hurricane',
  'tornado',
  'wildfire',
  'earthquake',
  'flood',
  'ice-storm',
];

/** Parse the JSON-encoded array on property.region_hazards_json. */
export function parseRegionHazards(json: string | null): RegionHazard[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((h): h is RegionHazard =>
      REGION_HAZARD_ORDER.includes(h as RegionHazard)
    );
  } catch {
    return [];
  }
}
